import {Component, ChangeDetectionStrategy, input, linkedSignal} from '@angular/core';

/**
 * A platform's initials: the first letter of each word ("Map Your Show" → MYS), one letter for a
 * single word, "?" for no name.
 */
export function platformInitials(name: string | null | undefined): string {
  if (typeof name !== 'string' || !name.trim()) {
    return '?';
  }
  return name.trim().split(/\s+/).map((part) => part.charAt(0).toUpperCase()).join('');
}

/**
 * A registration platform's logo, or its initials when it has none. The initials also show while
 * the image loads (a remote logo has no size until then, so the tile would sit empty) and when it
 * fails to load.
 */
@Component({
  selector: 'app-registration-platform-logo',
  template: `
    <span
      class="rp-logo"
      [class.rp-logo--sm]="size() === 'sm'"
      [class.rp-logo--image]="imageState() === 'loaded'"
      aria-hidden="true">
      @if (image() && imageState() !== 'failed') {
      <img
        class="rp-logo__img"
        [class.rp-logo__img--loading]="imageState() !== 'loaded'"
        [src]="image()"
        alt=""
        (load)="imageState.set('loaded')"
        (error)="imageState.set('failed')">
      }
      @if (imageState() !== 'loaded') {
      <span
        class="rp-logo__initials"
        [class.rp-logo__initials--single]="initials.length === 1">{{ initials }}</span>
      }
    </span>`,
  styleUrls: ['./registration-platform-logo.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RegistrationPlatformLogoComponent {
  readonly name = input<string | null>('');
  readonly image = input<string | null>(null);
  readonly size = input<'sm' | 'md'>('md');

  /** The image's progress; `none` without an image. */
  readonly imageState = linkedSignal<'none' | 'loading' | 'loaded' | 'failed'>(() => this.image() ? 'loading' : 'none');

  get initials(): string {
    return platformInitials(this.name());
  }
}
